import {Button} from "../../basic/button";
import {Label} from "../../basic/label";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "../../basic/select";
import {useTranslationWithKeys} from "../../../hooks/useTranslationWithKeys";
import {VwpLicense} from "../../../store/generated/api";

interface LicenseSelectorProps {
    value: string;
    onValueChange: (value: string) => void;
    licenses: VwpLicense[];
    label?: string;
    placeholder?: string;
    showNoneOption?: boolean;
    disabled?: boolean;
    actionButton?: {
        text: string;
        onClick: () => void;
        disabled?: boolean;
        loading?: boolean;
        icon?: React.ReactNode;
        variant?: "default" | "destructive" | "outline" | "secondary" | "ghost" | "link";
    };
    className?: string;
    showAvailableCount?: boolean;
    description?: string;
    width?: string;
}

export function LicenseSelector({
                                    value,
                                    onValueChange,
                                    licenses,
                                    label,
                                    placeholder,
                                    showNoneOption = false,
                                    disabled = false,
                                    actionButton,
                                    className = "",
                                    showAvailableCount = true,
                                    description,
                                    width = "w-80"
                                }: LicenseSelectorProps) {
    const {t} = useTranslationWithKeys();
    const getAvailableCount = (license: VwpLicense) => {
        if (license.available !== undefined) return license.available;
        if (license.quantity !== undefined && license.assigned !== undefined) {
            return license.quantity - license.assigned;
        }
        return 0;
    };

    const getLicenseDisplayText = (license: VwpLicense) => {
        if (showAvailableCount) {
            const available = getAvailableCount(license);
            const total = license.quantity || 0;
            return `${license.product} ${available}/${total} (${license.id})`;
        }
        return `${license.product} (${license.id})`;
    };

    return (
        <div className={`space-y-2 ${className}`}>
            {label && <Label>{label}</Label>}
            <div className="flex gap-2">
                <Select value={value} onValueChange={onValueChange} disabled={disabled}>
                    <SelectTrigger className={`min-w-0 max-w-full ${width}`}>
                        <SelectValue placeholder={placeholder || t('common.team.selectLicense')}/>
                    </SelectTrigger>
                    <SelectContent className={width} align="start">
                        {showNoneOption && (
                            <SelectItem value="none">{t('common.team.noLicenseAssigned')}</SelectItem>
                        )}
                        {licenses.map((license) => (
                            <SelectItem key={license.id} value={license.id}>
                                {getLicenseDisplayText(license)}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                {actionButton && (
                    <Button
                        className="relative"
                        onClick={actionButton.onClick}
                        disabled={actionButton.disabled || disabled || actionButton.loading}
                        variant={actionButton.variant || "default"}
                    >
                        <span className={`inline-flex items-center gap-2 ${actionButton.loading ? "invisible" : ""}`}>
                            {actionButton.icon}
                            {actionButton.text}
                        </span>
                        {actionButton.loading && (
                            <span className="absolute inset-0 flex items-center justify-center">
                                <span
                                    className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"/>
                            </span>
                        )}
                    </Button>
                )}
            </div>
            {description && (
                <p className="text-muted-foreground">
                    {description}
                </p>
            )}
        </div>
    );
}